import { inject, Injectable } from "@angular/core";
import { User } from "@modules/user-management/_models/user-management-api.interface";
import { UserService } from "@modules/user-management/_services/user.service";
import { UuidUtil } from "@shared/utils/uuid.util";
import { catchError, forkJoin, map, mergeMap, Observable, of, shareReplay, take } from "rxjs";

@Injectable({
	providedIn: "root"
})
export class UserNameService {
	/** In-memory cache with TTL and coalescing */
	private userCache = new Map<string, { obs$: Observable<string>; expires: number }>();
	private readonly ttlMs = 5 * 60 * 1000; // 5 minutes; tweak as needed

	private userService = inject(UserService);

	getUserNameFromId(userId: string): Observable<string> {
		if (userId === "SYSTEM" || UuidUtil.isNotUuid(userId)) return of("SYSTEM");

		const now = Date.now();
		const existing = this.userCache.get(userId);
		if (existing && existing.expires > now) {
			return existing.obs$; // reuse cached observable
		}

		// Create a shared observable that coalesces concurrent subscribers and caches the value
		const obs$ = this.userService.getUserById(userId).pipe(
			take(1),
			mergeMap((res: User | null) => (res ? this.fullNameFromId(res) : this.systemName())),
			catchError(() => this.systemName()),
			shareReplay({ bufferSize: 1, refCount: false, windowTime: this.ttlMs }) // cache result for TTL
		);

		this.userCache.set(userId, { obs$, expires: now + this.ttlMs });
		return obs$;
	}

	private fullNameFromId(user: User): Observable<string> {
		const fullName = `${user.firstName} ${user.lastName}`;
		return of(fullName);
	}

	private systemName(): Observable<string> {
		return of("System");
	}

	/** Batch helper: resolves a set of userIds into a Map<userId, userName> */
	resolveUserNames(userIds: string[]): Observable<Map<string, string>> {
		const uniqueIds = Array.from(new Set(userIds));
		const requests$ = uniqueIds.map(id => this.getUserNameFromId(id).pipe(map(name => ({ id, name }))));

		return forkJoin(requests$).pipe(
			map(entries => {
				const mapResult = new Map<string, string>();
				for (const { id, name } of entries) mapResult.set(id, name);
				return mapResult;
			})
		);
	}

	clearUserCache(userId: string): void {
		if (!userId) return;
		this.userCache.delete(userId);
	}
}
